import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert } from 'antd';
import { AppBarCMS } from '../../../../basic/index';
import {
  Spinner,
  CollapseBlock,
  QuestionModal,
} from '../../../../common/index.jsx';
import { IconButton } from '../../../../common/controls/Controls.jsx';
import {
  FieldTimeOutlined,
  StopOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { QUIZ_QUESTION_DEFAULT_COUNTDOWN_MINUTES } from '../../../../../utils/constants/common_constants.js';
import { QRCodeCanvas } from 'qrcode.react';
import { routes } from '../../../../../utils/helpers/ApiClient.jsx';
import FormTime from '../../../../antd/form/FormTimePicker/FormTime.jsx';
import {
  useGetQuizQuestionQuery,
  useGetQuizQuestionAnswersStatsQuery,
  useGetQuizQuestionCorrectAnswersQuery,
  useGetQuizQuestionFastestAnswersQuery,
} from '../../../../../redux/services/quiz_questions.jsx';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import {
  useSaveQuizRunMutation,
  useUpdateQuizRunMutation,
} from '../../../../../redux/services/quiz_runs.jsx';
import { useGetAggregatedStudentQuizCorrectAnswersQuery } from '../../../../../redux/services/quiz_student_answers.jsx';
import QuizQuestionAnswersChart from '../Results/QuizQuestionAnswersChart.jsx';

const getDefaultCountdownTime = () =>
  dayjs().hour(0).minute(QUIZ_QUESTION_DEFAULT_COUNTDOWN_MINUTES).second(0);

const QuestionQRPage = () => {
  const { id } = useParams();
  const questionId = id ? Number(id) : undefined;
  const { t } = useTranslation();
  const { successMessage, errorMessage } = useToastMessages();
  const [saveQuizRun] = useSaveQuizRunMutation();
  const [updateQuizRun] = useUpdateQuizRunMutation();

  // State
  const [cdTime, setCdTime] = React.useState(getDefaultCountdownTime);
  const [remainingSec, setRemainingSec] = React.useState(null);
  const [finished, setFinished] = React.useState(false);
  const [runId, setRunId] = React.useState(null);
  const [runFrom, setRunFrom] = React.useState(null);
  const [showLeaveModal, setShowLeaveModal] = React.useState(false);
  const [pendingHref, setPendingHref] = React.useState(null);

  // Data
  const { data, error: questionError, isLoading } = useGetQuizQuestionQuery(
    questionId,
    {
      skip: !questionId,
    },
  );
  const questionErrorStatus = questionError?.status;
  const shouldShowInactiveAlert =
    questionErrorStatus === 403 || questionErrorStatus === 404 || !data?.text;

  // Correct answers only after run finished
  const { data: correctAnswers } = useGetQuizQuestionCorrectAnswersQuery(
    { id: Number(questionId), run_id: runId },
    { skip: !(finished && questionId && runId) },
  );

  // Poll stats per current run
  const isCountingDown =
    questionId && remainingSec !== null && remainingSec > 0 && !!runId;
  const { data: stats, refetch: refetchStats } =
    useGetQuizQuestionAnswersStatsQuery(
      { id: Number(questionId), run_id: runId },
      {
        skip: !(questionId && runId),
        pollingInterval: isCountingDown ? 2000 : 0,
      },
    );
  const { data: fastestAnswers } = useGetQuizQuestionFastestAnswersQuery(
    { id: Number(questionId), run_id: runId },
    {
      skip: !(questionId && runId),
      pollingInterval: isCountingDown ? 5000 : 0,
    },
  );
  // New aggregated query: active while run exists (poll only during countdown)
  const {
    data: aggregatedData,
    isLoading: isAggregatedLoading,
    refetch: refetchAggregated,
  } = useGetAggregatedStudentQuizCorrectAnswersQuery(
    { fv_question_id: questionId, run_id: runId },
    {
      skip: !(questionId && runId),
      pollingInterval: isCountingDown ? 5000 : 0,
    },
  );
  const rows = React.useMemo(
    () =>
      Array.isArray(aggregatedData)
        ? aggregatedData
        : Array.isArray(aggregatedData?.data)
          ? aggregatedData.data
          : [],
    [aggregatedData],
  );

  useEffect(() => {
    setCdTime(getDefaultCountdownTime());
    setRemainingSec(null);
    setFinished(false);
    setRunId(null);
    setRunFrom(null);
  }, [questionId]);

  useEffect(() => {
    if (remainingSec === null || remainingSec <= 0) return;
    const id = setInterval(() => {
      setRemainingSec((prev) => (prev !== null && prev > 0 ? prev - 1 : prev));
    }, 1000);
    return () => clearInterval(id);
  }, [remainingSec]);

  useEffect(() => {
    if (remainingSec === 0) {
      setFinished(true);
      // ensure we capture final answers snapshot
      if (questionId && runId) {
        setTimeout(() => refetchAggregated(), 0);
      }
    }
  }, [remainingSec, questionId, runId, refetchAggregated]);

  const startCountdown = () => {
    if (!cdTime) return;
    const mins = cdTime.minute();
    const secs = cdTime.second();
    const total = Math.max(0, mins * 60 + secs);
    if (total > 0) {
      setFinished(false);
      setRemainingSec(total);
    }
  };

  const handleStart = async () => {
    if (!cdTime || !questionId) return;
    try {
      const seconds = cdTime.minute() * 60 + cdTime.second();
      if (seconds <= 0) return;
      const from = dayjs();
      const to = from.add(seconds, 'second');
      const payload = {
        fv_question_id: Number(questionId),
        date_valid_from: from.format('YYYY-MM-DD HH:mm:ss'),
        date_valid_to: to.format('YYYY-MM-DD HH:mm:ss'),
      };
      const res = await saveQuizRun(payload).unwrap();
      if (res && res.id) {
        setRunId(res.id);
        setRunFrom(payload.date_valid_from);
      }
      successMessage({ message: t('Spustenie odpočtu bolo uložené.') });
      startCountdown();
    } catch (e) {
      errorMessage({ message: t('Ukladanie spustenia odpočtu zlyhalo.') });
    }
  };

  const stopCountdown = async () => {
    const wasRunning = remainingSec !== null && remainingSec > 0;
    setRemainingSec(null);
    setFinished(true);
    if (runId && runFrom && questionId) {
      try {
        const now = dayjs().format('YYYY-MM-DD HH:mm:ss');
        await updateQuizRun({
          id: runId,
          body: {
            fv_question_id: Number(questionId),
            date_valid_from: runFrom,
            date_valid_to: now,
          },
        }).unwrap();
        await refetchStats();
        await refetchAggregated(); // refresh final distribution
        if (wasRunning) {
          successMessage({ message: t('Ukončenie odpočtu bolo uložené.') });
        }
      } catch (e) {
        errorMessage({ message: t('Ukladanie ukončenia odpočtu zlyhalo.') });
      }
    }
  };

  const formatRemaining = (s) => {
    const mm = Math.floor(s / 60);
    const ss = s % 60;
    return `${mm}:${ss.toString().padStart(2, '0')}`;
  };

  const url = questionId
    ? `${window.location.origin}${routes.cmsQuizQuestionAnswer}/${questionId}`
    : '';
  const adminUrl = questionId
    ? `${window.location.origin}${routes.cmsQuizQuestionQR}/${questionId}`
    : '';

  const copyAdminUrl = async () => {
    if (!adminUrl) return;
    try {
      await navigator.clipboard.writeText(adminUrl);
      successMessage({
        message: t('Odkaz na zobrazenie QR kódu bol skopírovaný do schránky.'),
      });
    } catch (e) {
      errorMessage({ message: t('Kopírovanie zlyhalo.') });
    }
  };

  const shouldWarnLeave =
    remainingSec !== null && remainingSec > 0 && !finished;

  // Warn on browser close/refresh while countdown is running
  React.useEffect(() => {
    if (!shouldWarnLeave) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [shouldWarnLeave]);

  // Intercept same-origin navigation via anchor clicks
  React.useEffect(() => {
    if (!shouldWarnLeave) return;
    const onClick = (e) => {
      if (e.defaultPrevented) return;
      if (e.button !== 0) return; // left-click only
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; // allow new tab/open
      const anchor = e.target.closest && e.target.closest('a[href]');
      if (!anchor) return;
      if (anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      const href = anchor.getAttribute('href');
      if (!href) return;
      let nextUrl;
      try {
        nextUrl = new URL(href, window.location.origin);
      } catch {
        return;
      }
      if (nextUrl.origin !== window.location.origin) return; // external ok
      // allow same-page hash only
      if (
        nextUrl.pathname === window.location.pathname &&
        nextUrl.search === window.location.search &&
        nextUrl.hash
      )
        return;
      e.preventDefault();
      setPendingHref(nextUrl.pathname + nextUrl.search + nextUrl.hash);
      setShowLeaveModal(true);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [shouldWarnLeave]);

  const currentHrefRef = React.useRef(
    window.location.pathname + window.location.search + window.location.hash,
  );
  useEffect(() => {
    // keep ref in sync on mount
    currentHrefRef.current =
      window.location.pathname + window.location.search + window.location.hash;
  }, []);

  // Intercept browser back/forward (popstate) while timer is running
  useEffect(() => {
    if (!shouldWarnLeave) return;
    const onPopState = () => {
      const intended =
        window.location.pathname +
        window.location.search +
        window.location.hash;
      // revert back immediately to keep UI stable
      const backTo = currentHrefRef.current;
      if (intended !== backTo) {
        history.pushState(null, '', backTo);
        setPendingHref(intended);
        setShowLeaveModal(true);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [shouldWarnLeave]);

  const confirmLeave = () => {
    const href = pendingHref;
    setShowLeaveModal(false);
    setPendingHref(null);
    if (href) window.location.href = href;
  };
  const cancelLeave = () => {
    setShowLeaveModal(false);
    setPendingHref(null);
    // keep currentHrefRef as-is
  };

  return (
    <div>
      <AppBarCMS
        title={t('Spustenie otázky cez QR kód')}
        backUrl={routes.cmsQuizzes}
        backUrlTooltip={t('Kvízy')}
        permissionsBasic={'quizzes'}
      />

      {isLoading && <Spinner />}
      {!isLoading && (
        <div style={{ padding: 16 }}>
          {shouldShowInactiveAlert && (
            <Alert
              type="warning"
              showIcon
              message={t('Otázku je možné zobraziť iba počas aktívneho runu.')}
            />
          )}

          {/* Otázka */}
          <CollapseBlock title={t('Kvízová otázka')} collapsible="false">
            {data?.text && (
              <div
                style={{
                  marginBottom: 12,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                }}
              >
                <div style={{ fontSize: '150%', flex: 1 }}>{data.text}</div>
                <IconButton
                  icon={<CopyOutlined />}
                  tooltip={t('Skopírovať odkaz na zobrazenie QR kódu')}
                  onClick={copyAdminUrl}
                  disabled={!adminUrl}
                />
              </div>
            )}

            {/* Correct answers shown under question after run finishes */}
            {finished && Array.isArray(data?.answers) && (
              <div style={{ marginTop: 12, width: '100%', textAlign: 'left' }}>
                {data.answers
                  .slice()
                  .sort((a, b) => (a.position || 0) - (b.position || 0))
                  .map((ans) => {
                    const isCorrect = (
                      correctAnswers?.correct_answer_ids || []
                    ).includes(ans.id);
                    return (
                      <div
                        key={ans.id}
                        style={{
                          fontSize: '150%',
                          marginBottom: 6,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {isCorrect ? (
                          <CheckCircleOutlined
                            style={{ color: '#52c41a', marginRight: 8 }}
                          />
                        ) : (
                          <CloseCircleOutlined
                            style={{ color: '#ff4d4f', marginRight: 8 }}
                          />
                        )}
                        <span
                          style={{
                            flex: 1,
                            color: isCorrect ? '#52c41a' : undefined,
                          }}
                        >
                          {ans.text}
                        </span>
                      </div>
                    );
                  })}
              </div>
            )}
          </CollapseBlock>

          {/* QR kód + odkaz + ovládacie prvky */}
          <CollapseBlock title={t('QR kód a časovač')} collapsible="false">
            {/* Controls row: time + start/stop (copy removed/moved) */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
                marginTop: 8,
                marginBottom: 8,
              }}
            >
              <FormTime
                inline
                label={t('Čas (mm:ss)')}
                value={cdTime}
                onChange={setCdTime}
                format="mm:ss"
                showNow={false}
                allowClear={false}
                minuteStep={1}
                secondStep={1}
                disabled={remainingSec !== null && remainingSec > 0}
                defaultOpenValue={dayjs().hour(0).minute(0).second(0)}
                size="large"
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                <IconButton
                  icon={<FieldTimeOutlined />}
                  tooltip={t('Spustiť odpočítavanie')}
                  style={{ color: 'green' }}
                  onClick={handleStart}
                  disabled={remainingSec !== null && remainingSec > 0}
                />
                <IconButton
                  icon={<StopOutlined />}
                  tooltip={t('Zastaviť odpočítavanie')}
                  style={{ color: 'red' }}
                  onClick={stopCountdown}
                  disabled={remainingSec === null || remainingSec <= 0}
                />
              </div>
            </div>

            {/* Centered QR and URL block; hide QR after finish */}
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              {url && !finished && (
                <>
                  <QRCodeCanvas value={url} size={400} />
                  <div style={{ marginTop: 8 }}>
                    <a href={url} target="_blank" rel="noopener noreferrer">
                      {url}
                    </a>
                  </div>
                </>
              )}

              {/* Timer */}
              {remainingSec !== null ? (
                <div style={{ marginTop: 16, fontSize: 56, fontWeight: 700 }}>
                  {formatRemaining(Math.max(0, remainingSec))}
                </div>
              ) : finished ? (
                <div style={{ marginTop: 16, fontSize: 56, fontWeight: 700 }}>
                  {t('Koniec')}
                </div>
              ) : null}
            </div>
          </CollapseBlock>

          {/* Výsledky kvízu */}
          <CollapseBlock title={t('Výsledky kvízu')} collapsible="false">
            <div
              style={{
                marginTop: 8,
                fontSize: 24,
                fontWeight: 700,
                margin: '0 auto',
                textAlign: 'center',
              }}
            >
              <span style={{ color: '#52c41a' }}>
                {t('Správne')}: {stats?.correct ?? 0}
              </span>
              <span style={{ padding: '0 8px' }}>•</span>
              <span style={{ color: '#ff4d4f' }}>
                {t('Nesprávne')}: {stats?.incorrect ?? 0}
              </span>
              <span style={{ padding: '0 8px' }}>•</span>
              <span style={{ color: '#000' }}>
                {t('Spolu')}: {stats?.total ?? 0}
              </span>
            </div>
            {/* Chart shown while running or after finish (last snapshot) */}
            {(isCountingDown || finished) && rows.length > 0 && (
              <div style={{ marginTop: 16 }}>
                {isCountingDown && isAggregatedLoading && <Spinner />}
                <QuizQuestionAnswersChart rows={rows} />
              </div>
            )}
            {fastestAnswers && fastestAnswers.length > 0 && (
              <div style={{ marginTop: 16, textAlign: 'center' }}>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: '150%',
                    color: '#000',
                    marginBottom: 12,
                  }}
                >
                  {t('Najrýchlejší študenti')}
                </div>
                <div
                  style={{
                    display: 'inline-block',
                    textAlign: 'left',
                    fontSize: '150%',
                    color: '#000',
                    lineHeight: 1.25,
                    minWidth: 320,
                  }}
                >
                  {fastestAnswers
                    .filter((f) => f.is_correct)
                    .slice(0, 3)
                    .map((f, idx) => {
                      const rawSec =
                        typeof f.seconds_from_start === 'number'
                          ? f.seconds_from_start
                          : 0;
                      const positiveSec = Math.max(0, (rawSec || 0) * -1);
                      const mm = Math.floor(positiveSec / 60)
                        .toString()
                        .padStart(2, '0');
                      const ss = (positiveSec % 60).toString().padStart(2, '0');
                      const displayTime = `${mm}:${ss}`;
                      const medal = ['🥇', '🥈', '🥉'][idx] || '';
                      return (
                        <div
                          key={f.user_id}
                          style={{
                            display: 'flex',
                            alignItems: 'baseline',
                            marginBottom: 8,
                            fontWeight: 500,
                            color: '#000',
                          }}
                        >
                          <span
                            style={{
                              width: '2.5ch',
                              display: 'inline-block',
                              textAlign: 'center',
                              marginRight: 12,
                            }}
                            title={medal ? t('Poradie') + ' ' + (idx + 1) : ''}
                          >
                            {medal}
                          </span>
                          <span style={{ flex: 1 }}>{f.full_name}</span>
                          <span
                            style={{
                              marginLeft: 16,
                              whiteSpace: 'nowrap',
                              fontWeight: 400,
                            }}
                          >
                            {displayTime}
                          </span>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </CollapseBlock>

          {/* Leave confirmation modal */}
          <QuestionModal
            chosenPositive={confirmLeave}
            question={t(
              'Odchod zo stránky preruší zobrazenie časovača. Na pozadí časovač bude bežať ďalej a študenti budú mocť až do skončenia času na otázku odpovedať. Naozaj chcete odísť?',
            )}
            visible={shouldWarnLeave && showLeaveModal}
            yes={t('Odísť')}
            toggle={cancelLeave}
            headerTitle={t('Upozornenie')}
          />
        </div>
      )}
    </div>
  );
};

export default QuestionQRPage;
